import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageSquareWarning, X, Send } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { submitProblemReport } from '../config/api';
import { useAuth } from '../contexts/AuthContext';

const CATEGORIES = [
  { value: 'BUG', label: '🐞 Something is broken' },
  { value: 'FEATURE', label: '💡 I want a feature / change' },
  { value: 'DATA_ISSUE', label: '📊 The data looks wrong' },
  { value: 'QUESTION', label: '❓ I have a question' },
  { value: 'OTHER', label: '✍️ Other' }
];

// Derive a human-readable screen name from the route so reports are easy to triage.
const screenNameFromPath = (path) => {
  const seg = path.replace(/^\/app\/?/, '').split('/').filter(Boolean);
  if (seg.length === 0) return 'Dashboard';
  return seg[0].replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

const ReportProblemButton = () => {
  const location = useLocation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState('BUG');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const screenTitle = screenNameFromPath(location.pathname);

  const reset = () => { setCategory('BUG'); setNotes(''); };

  const handleSubmit = async () => {
    if (!notes.trim()) { toast.error('Please describe the problem'); return; }
    setSubmitting(true);
    try {
      await submitProblemReport({
        notes: notes.trim(),
        category,
        screen: location.pathname,
        screenTitle,
        pageUrl: window.location.href,
        userAgent: navigator.userAgent,
        context: {
          route: location.pathname,
          search: location.search || null,
          role: user?.roles?.[0]?.role?.code || user?.role || null,
          viewport: `${window.innerWidth}x${window.innerHeight}`,
          referrer: document.referrer || null,
          reportedAt: new Date().toISOString()
        }
      });
      toast.success('Thanks! Your report was submitted.');
      reset();
      setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.message || 'Failed to submit report');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      {/* Floating trigger — bottom-left so it doesn't collide with the help widget. */}
      <button
        onClick={() => setOpen(true)}
        title="Report a problem on this screen"
        className="fixed bottom-5 left-5 z-40 flex items-center gap-2 rounded-full bg-rose-600 px-4 py-2.5 text-sm font-medium text-white shadow-lg hover:bg-rose-700 focus:outline-none focus:ring-2 focus:ring-rose-400"
      >
        <MessageSquareWarning className="h-4 w-4" />
        Report Problem
      </button>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" onClick={() => !submitting && setOpen(false)}>
          <div className="w-full max-w-lg rounded-lg bg-white shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4">
              <div className="flex items-center gap-2">
                <MessageSquareWarning className="h-5 w-5 text-rose-600" />
                <h3 className="text-lg font-semibold text-gray-900">Report a Problem</h3>
              </div>
              <button onClick={() => setOpen(false)} disabled={submitting} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>

            <div className="space-y-4 px-5 py-4">
              <div className="rounded-md bg-gray-50 px-3 py-2 text-xs text-gray-600">
                Screen: <span className="font-medium text-gray-800">{screenTitle}</span>
                <span className="mx-1">·</span>
                <span className="font-mono">{location.pathname}</span>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">What kind of issue is it?</label>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                >
                  {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Tell us what happened or what you need <span className="text-rose-500">*</span>
                </label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  rows={5}
                  autoFocus
                  placeholder="e.g. The page count at the bottom is wrong, or: I need a way to bulk-approve claims…"
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
                />
                <p className="mt-1 text-xs text-gray-400">
                  We automatically attach the screen, URL, and your account so the team can reproduce it.
                </p>
              </div>
            </div>

            <div className="flex justify-end gap-3 border-t border-gray-200 px-5 py-4">
              <button
                onClick={() => setOpen(false)}
                disabled={submitting}
                className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="inline-flex items-center gap-2 rounded-md bg-rose-600 px-4 py-2 text-sm font-medium text-white hover:bg-rose-700 disabled:opacity-50"
              >
                <Send className="h-4 w-4" />
                {submitting ? 'Submitting…' : 'Submit Report'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReportProblemButton;
